import React from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../common/hooks/hooks";
import { useGetTagsQuery } from "../../../api/productTaxonomyApi";
import { toggleTag } from "../../../productSlice";

const TagFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // --- Data Fetching & State Management ---
  const {
    data: tagsData,
    isLoading: isTagsLoading,
    isError: isTagsError,
  } = useGetTagsQuery();

  const isComponentLoading = isTagsLoading || !areTranslationsReady;
  const selectedTags = useAppSelector((state) => state.product.filters.tag);

  // Memoized selector to get the top 8 most used tags, sorted alphabetically.
  const topTags = React.useMemo(() => {
    if (!tagsData?.tags) return [];

    // 1. Get all tags, 2. Filter active ones, 3. Sort by usage count (desc)
    const sortedByCount = Object.values(tagsData.tags)
      .filter((tag) => tag.count > 0)
      .sort((a, b) => b.count - a.count);

    // 4. Take the top 8, and 5. Sort them alphabetically for display
    return sortedByCount
      .slice(0, 8)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tagsData]);

  const handleTagClick = (tagId: number) => {
    dispatch(toggleTag(tagId));
  };

  // If loading is finished, there's no error, and there are no tags to show, render nothing.
  if (!isComponentLoading && !isTagsError && topTags.length === 0) {
    return null;
  }

  return (
    <div className="filter-widget">
      <h3 className="filter-widget__title">
        {isComponentLoading ? (
          <Skeleton width={120} />
        ) : (
          t("productsArchive.sidebar.tagTitle")
        )}
      </h3>

      {/* State 1: Loading Skeleton */}
      {isComponentLoading && (
        <div className="filter-widget__pill-container">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              width={Math.random() * (100 - 60) + 60} // Random width for realism
              height={28}
              borderRadius="999px"
            />
          ))}
        </div>
      )}

      {/* State 2: isFetching state is intentionally ignored to not cause UI flicker during background refetches,
       since taxonomy data rarely change. */}

      {/* State 3: API Error (will only show if there's an error and not loading) */}
      {!isComponentLoading && isTagsError && (
        <p>{t("productsArchive.sidebar.tagError")}</p>
      )}

      {/* State 4: (No Tags Found) is handled by returning null above */}

      {/* State 5: Success with Tags (will only show if not loading, no error, and topTags has items) */}
      {!isComponentLoading && !isTagsError && topTags.length > 0 && (
        <div className="filter-widget__pill-container">
          {topTags.map((tag) => {
            const isSelected = selectedTags.includes(tag.id);
            return (
              <button
                key={tag.id}
                className={`filter-widget__pill ${
                  isSelected ? "is-active" : ""
                }`}
                onClick={() => handleTagClick(tag.id)}
                aria-pressed={isSelected}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TagFilter;
